import {Middleware, configureStore} from '@reduxjs/toolkit';

import apiMiddleware from '@pb/middleware-api';

import {getCachedAuthTokens} from '../utils/auth-tokens';

import {rootReducer} from './root-reducer';

type RootState = ReturnType<typeof rootReducer>;

type ActionTypesTuple = Array<{type?: string}>;

type MiddlewareAction = {
    type?: string | ((...args: unknown[]) => unknown) | {type?: string},
    types?: ActionTypesTuple,
    responseData?: unknown,
    response?: {ok?: boolean} | unknown,
    approvalRequestSearchCriteria?: unknown,
};

const actionTypeFixMiddleware: Middleware = store => next => (action: unknown) => {
    const mwAction = action as MiddlewareAction;
    if (!mwAction.type && Array.isArray(mwAction.types)) {
        const [, successCreator, failureCreator] = mwAction.types as ActionTypesTuple;
        const isOk = (mwAction.response as {ok?: boolean} | undefined)?.ok;
        const meta = {approvalRequestSearchCriteria: mwAction.approvalRequestSearchCriteria};
        if (isOk && successCreator?.type) {
            return store.dispatch({
                type: successCreator.type,
                payload: {data: mwAction.responseData, meta, error: false, success: true},
            });
        }
        if (!isOk && failureCreator?.type) {
            return store.dispatch({
                type: failureCreator.type,
                payload: {data: mwAction.responseData, meta, error: true, success: false},
            });
        }
        return;
    }
    if (typeof mwAction.type === 'function' && (mwAction.type as {type?: string}).type) {
        return next({...mwAction, type: (mwAction.type as {type?: string}).type});
    }
    return next(action);
};

const authMiddleware: Middleware = store => next => (action: unknown) => {
    const apiAction = action as {CALL_API?: {headers?: Record<string, string>}};
    if (apiAction?.CALL_API) {
        let accessToken: string | null = null;
        let idToken: string | null = null;
        const state = store.getState() as RootState;
        const userInfo = (state as unknown as {userInfo?: {idToken?: string, accessToken?: string}}).userInfo;
        if (userInfo?.idToken) {
            idToken = userInfo.idToken;
        }
        if (userInfo?.accessToken) {
            accessToken = userInfo.accessToken;
        }
        if (!accessToken || !idToken) {
            const cached = getCachedAuthTokens();
            accessToken = accessToken ?? cached.accessToken;
        }
        if (accessToken && apiAction.CALL_API) {
            apiAction.CALL_API.headers = apiAction.CALL_API.headers || {};
            apiAction.CALL_API.headers.Authorization = `Bearer ${accessToken}`;
        }
    }
    return next(action);
};

export const getStoreWithoutDefaults = (preloadedState = {} as RootState) => (
    configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({serializableCheck: false}),
        preloadedState,
    })
);

export const store = configureStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActionPaths: [
                    'CALL_API.types',
                    'CALL_API.headers',
                    'CALL_API.method',
                    'CALL_API.endpoint',
                ],
            },
        }).concat([
            authMiddleware,
            apiMiddleware({apiRoot: process.env.REACT_APP_API_ROOT || ''}) as Middleware,
            actionTypeFixMiddleware,
        ]),
});
